import { FC, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/layouts/modal';
import useApi from 'utils/use-api';
import wrapClick from 'utils/wrap-click';
import SelectInput from 'components/core/select-input';
import TextInput from 'components/core/text-input';
import dayjs from 'dayjs';

interface GenerateReportModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string | null;
}

const validationSchema = Yup.object({
  userId: Yup.number().required('Please select a user'),
  startDate: Yup.date().required('Start date is required'),
  endDate: Yup.date().required('End date is required').min(Yup.ref('startDate'), 'End date must be after start date'),
});

const GenerateReportModal: FC<GenerateReportModalProps> = ({ open, setOpen }) => {
  const api = useApi();
  const [downloading, setDownloading] = useState(false);

  const { data: users, isLoading: loadingUsers } = useQuery<User[]>({
    queryKey: ['all-users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data.rows || [];
    },
    enabled: open,
  });

  const form = useFormik({
    initialValues: {
      userId: '',
      startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
    validationSchema,
    onSubmit: async values => {
      await generateReportMutation.mutateAsync(values);
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (values: typeof form.values) => {
      setDownloading(true);
      const response = await api.post(
        '/reports/access-requests',
        {
          userId: Number(values.userId),
          startDate: values.startDate,
          endDate: values.endDate,
        },
        {
          responseType: 'blob',
        },
      );
      return response.data;
    },
    onSuccess: blob => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `access-request-report-${form.values.userId}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloading(false);
      setOpen(false);
      form.resetForm();
    },
    onError: () => {
      setDownloading(false);
    },
  });

  const userOptions =
    users?.map(user => ({
      label: user.fullName || `${user.firstName} ${user.lastName}`,
      value: user.id.toString(),
    })) || [];

  const renderActions = () => {
    return (
      <div className="flex gap-x-3 ml-3">
        <button
          type="button"
          onClick={wrapClick(() => form.handleSubmit())}
          disabled={downloading || !form.isValid}
          className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Generate Access Request Report"
      description="Select a user and date range to generate a PDF report"
      size="4xl"
      renderActions={renderActions}
    >
      <div className="space-y-6 mt-4">
        <div>
          <SelectInput
            id="userId"
            label="Select User"
            placeholder={loadingUsers ? 'Loading users...' : 'Choose a user...'}
            required={true}
            options={userOptions}
            disabled={loadingUsers}
            values={form.values}
            errors={form.errors}
            touched={form.touched}
            handleChange={form.handleChange}
            handleBlur={form.handleBlur}
          />
          <p className="mt-2 text-sm text-gray-500">Choose the user whose access requests you want to include in the report.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <TextInput
              id="startDate"
              label="Start Date"
              type="date"
              required={true}
              values={form.values}
              errors={form.errors}
              touched={form.touched}
              handleChange={form.handleChange}
              handleBlur={form.handleBlur}
            />
          </div>
          <div>
            <TextInput
              id="endDate"
              label="End Date"
              type="date"
              required={true}
              values={form.values}
              errors={form.errors}
              touched={form.touched}
              handleChange={form.handleChange}
              handleBlur={form.handleBlur}
            />
          </div>
        </div>

        {generateReportMutation.isError && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">
              {(generateReportMutation.error as any)?.response?.data?.message || 'Failed to generate report. Please try again.'}
            </p>
          </div>
        )}

        <div className="rounded-md bg-blue-50 p-4">
          <p className="text-sm text-blue-800">
            The report will include all access requests where the selected user is either the requester or the assignee, within the specified date
            range. The PDF will be downloaded automatically once generated.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default GenerateReportModal;
